import axios from 'axios';
import moment from 'moment';

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';

import { ILocation, ISessionKey, IBounds, IActivitiesRecentSearches, IClientCash } from '@share/common-types';
import { DEFAULT_PAGE_NUMBER, Urls, SESSION_EXPIRED_STATUS, SESSION_EXPIRED_MESSAGE, SESSION_KEY_LABEL, ACTIVITY_RECENT_SEARCHES_LABEL } from '@share/constants';
import { getHeaders, axiosInstance, getActivitySearchBody, UrlUtils, getTimeout, delay, getSelectedCurrency, hasChangedCurrency, getActivityRecentPreviewImages, getRecentSearchLocationKeyHotel, RootState, AppDispatch } from '@share/utils';

import { datesActions } from './dates';
import { roomsActions } from './rooms';
import { activityLocationActions } from './activity-locations';
import { activityFiltersActions } from './activity-filters';
import { activitySavedPropertyActions } from './activity-saved-property';
import { NULL_VALUE } from '@constants';
import { getLocationByCode } from '@share/services';

import { IActivity, IActivityCounters } from '@common-types';
const oneItem = 1;
const zeroItem = 0;
const reRenderNum = 0.000000001;

export interface IActivityState {
  activities: IActivity[];
  similarActivities: IActivity[];
  mapActivities: IActivity[];
  isMapView: boolean;
  loading: boolean;
  loadingSimilar: boolean;
  mapLoading: boolean;
  loadingMore: boolean;
  loadingFilter: boolean;
  error: string;
  sessionKey?: ISessionKey;
  similarSessionKey?: ISessionKey;
  isSearch: boolean;
  isWidget: boolean;
  pageNumber: number;
  searchedLocation: string;
  lastSearchedLocation: ILocation;
  counters: IActivityCounters | null | undefined;
  filterCounters: IActivityCounters | null | undefined;
  similarCounters: IActivityCounters | null | undefined;
  mapCounters: IActivityCounters;
  prevActivities: IActivity[];
  isSessionExpired: boolean;
  bounds: IBounds;
  boundsSimilar: IBounds;
  recentSearches: IActivitiesRecentSearches[];
  errorRecentSearches: string;
  loadingRecentSearches: boolean;
  referenceNumber: string;
  selectedActivity: IActivity;
  agencyNumber: string;
  selectedActivitySearchClientCash: IClientCash;
}

const initialState: IActivityState = {
  activities: NULL_VALUE as any,
  similarActivities: NULL_VALUE as any,
  mapActivities: NULL_VALUE as any,
  isMapView: false,
  loading: false,
  loadingSimilar: false,
  loadingMore: false,
  mapLoading: false,
  loadingFilter: false,
  error: '',
  isSearch: false,
  isWidget: false,
  pageNumber: DEFAULT_PAGE_NUMBER,
  searchedLocation: '',
  counters: null,
  filterCounters: null,
  similarCounters: null,
  mapCounters: NULL_VALUE as any,
  lastSearchedLocation: NULL_VALUE as any,
  bounds: NULL_VALUE as any,
  boundsSimilar: NULL_VALUE as any,
  prevActivities: [],
  isSessionExpired: false,
  recentSearches: [],
  errorRecentSearches: '',
  loadingRecentSearches: false,
  referenceNumber: NULL_VALUE,
  selectedActivity: NULL_VALUE as any,
  agencyNumber: NULL_VALUE,
  selectedActivitySearchClientCash: NULL_VALUE as any,
};

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    setIsSearch: (state: IActivityState, { payload }: PayloadAction<boolean>) => {
      state.isSearch = payload;
    },

    setBounds: (state: IActivityState, { payload }: PayloadAction<IBounds>) => {
      state.bounds = payload;
    },
    setLoading: (state: IActivityState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },

    setLastSearchedLocation: (state: IActivityState, { payload }: PayloadAction<ILocation>) => {
      state.lastSearchedLocation = payload;
    },

    setError: (state: IActivityState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setActivities: (
      state: IActivityState,
      { payload }: PayloadAction<{ activities: IActivity[]; isLoadMore: boolean }>,
    ) => {
      state.prevActivities = payload.activities;
      state.selectedActivity = NULL_VALUE;

      if (payload.isLoadMore) {
        state.activities = [...(state.activities || []), ...payload.activities];
      } else {
        state.activities = payload.activities;
      }
    },
    resetActivities: (state: IActivityState) => {
      state.prevActivities = [];
      state.activities = [];
      state.similarActivities = [];
      state.selectedActivity = NULL_VALUE;
      state.error = '';
      state.errorRecentSearches = '';
    },
    clearActivities: (state: IActivityState) => {
      state.prevActivities = NULL_VALUE;
      state.activities = NULL_VALUE;
      state.similarActivities = NULL_VALUE;
      state.selectedActivity = NULL_VALUE;
      state.error = '';
      state.errorRecentSearches = '';
    },
    setSessionKey: (state: IActivityState, { payload }: PayloadAction<ISessionKey>) => {
      state.sessionKey = payload;
    },
    setLoadingFilter: (state: IActivityState, { payload }: PayloadAction<boolean>) => {
      state.loadingFilter = payload;
    },
    setPageNumber: (state: IActivityState, { payload }: PayloadAction<number | undefined>) => {

      state.pageNumber = payload ? payload : state.pageNumber + oneItem;
    },
    setSearchedLocation: (state: IActivityState, { payload }: PayloadAction<string>) => {
      state.searchedLocation = payload;
    },
    setSelectedActivitySearchClientCash: (state: IActivityState, { payload }: PayloadAction<IClientCash>) => {
      state.selectedActivitySearchClientCash = payload;
    },
    setCounters: (state: IActivityState, { payload }: PayloadAction<IActivityCounters>) => {
      state.counters = payload;
    },
    setFilterCounters: (state: IActivityState, { payload }: PayloadAction<IActivityCounters | null>) => {
      state.filterCounters = payload;
    },
    setIsSessionExpired: (state: IActivityState, { payload }: PayloadAction<boolean>) => {
      state.isSessionExpired = payload;
    },
    setSelectedActivity: (state: IActivityState, { payload }: PayloadAction<IActivity>) => {
      state.selectedActivity = payload;
    },
    setMapLoading: (state: IActivityState, { payload }: PayloadAction<boolean>) => {
      state.mapLoading = payload;
    },
    setRecentSearches: (
      state: IActivityState,
      { payload }: PayloadAction<IActivityState['recentSearches']>,
    ) => {
      state.recentSearches = payload;
    },
    setLoadingMore: (state: IActivityState, { payload }: PayloadAction<boolean>) => {
      state.loadingMore = payload;
    },
    setLoadingRecentSearches: (state: IActivityState, { payload }: PayloadAction<boolean>) => {
      state.loadingRecentSearches = payload;
    },
    setIsMapView: (state: IActivityState, { payload }: PayloadAction<boolean>) => {
      state.isMapView = payload;
    },
    setErrorRecentSearches: (state: IActivityState, { payload }: PayloadAction<string>) => {
      state.errorRecentSearches = payload;
    },
  },
});

export const activityActions = activitySlice.actions;

export const activityReducer = activitySlice.reducer;

// let cancelRequest: Canceler;
let expirationTimer: number;
let abortController: AbortController | null = null;

export const setActivitySessionExpirationTimer = (session: ISessionKey, dispatch: AppDispatch): void => {
  if (expirationTimer) {
    clearTimeout(expirationTimer);
  }

  // @ts-ignore
  expirationTimer = setTimeout(() => {
    dispatch(activityActions.setIsSessionExpired(true));
  }, getTimeout(session?.expireDate));
};

export const resetActivity = createAsyncThunk<any, void, { state: RootState }>(
  'activity-search/resetActivity',
  async (_, { dispatch, getState }) => {
    const { activityStore } = getState();

    if (!activityStore.isMapView) {
      dispatch(activityActions.setLoading(true));
    }
    dispatch(activityActions.resetActivities());
  }
);





const getActivityCall = (locationToSearch: ILocation, isLoadMore: boolean, isReuseSessionKey: boolean, getState: any, signal: AbortSignal) => {
  const { datesStore, roomsStore, loginStore } = getState();
  const { startDate, endDate } = datesStore;
  const { roomsSelected } = roomsStore;
  const { lifeStyle } = loginStore;
  const Residency = 'US';
  return axiosInstance.post(
    Urls.ActivitySearch,
    getActivitySearchBody(
      locationToSearch,
      startDate,
      endDate,
      roomsSelected,
      getState().activityStore,
      getState().activityFiltersStore,
      isLoadMore,
      hasChangedCurrency() ? false : isReuseSessionKey,
      getSelectedCurrency(loginStore.account),
      lifeStyle,
      Residency,
    ),
    {
      ...getHeaders(),
      signal,
    },
  );
}

const getActivityRetry = async (locationToSearch: ILocation, isLoadMore: boolean, isReuseSessionKey: boolean, getState: any, signal: AbortSignal) => {
  try {
    return await getActivityCall(locationToSearch, isLoadMore, isReuseSessionKey, getState, signal);
  } catch (error) {
    await delay(300);
    return await getActivityCall(locationToSearch, isLoadMore, isReuseSessionKey, getState, signal);
  }
}

export const initializeActivitySearch = createAsyncThunk<any, void, { state: RootState }>(
  'activity-search/initializeActivitySearch',
  async (_, { dispatch }) => {
    dispatch(activityActions.setIsSearch(true));
    dispatch(activityActions.setLoading(true));
    dispatch(activityActions.setActivities({ activities: [], isLoadMore: false }));
  }
)

export const mergeActivityCounters = createAsyncThunk<any, { counters: any, filterCounter: any, filterCountersUpcomming: any }, { state: RootState }>(
  'activity-search/mergeActivityCounters',
  async ({ counters, filterCounter, filterCountersUpcomming }, { dispatch }) => {
    const countersMerge = filterCounter ? { ...filterCounter } : counters ? { ...counters } : null;
    if (filterCountersUpcomming && countersMerge) {
      Object.keys(filterCountersUpcomming).forEach(key => {
        const typedKey = key as keyof typeof filterCountersUpcomming;
        const counterKey = key as keyof typeof countersMerge;

        if (countersMerge?.hasOwnProperty(key)) {
          const filteredItemsCounters = filterCountersUpcomming?.[typedKey];
          /** Nested category tree: replace wholesale from API when filtering */
          if (counterKey === 'categories') {
            countersMerge[counterKey] = Array.isArray(filteredItemsCounters) ? filteredItemsCounters : [];
            return;
          }
          if (Array.isArray(countersMerge[counterKey])) {
            const filteredItems = filteredItemsCounters;

            countersMerge[counterKey] = [
              ...countersMerge[counterKey]
                .filter((item: any) => !filteredItems?.map((f: any) => JSON.stringify(f.key)).includes(JSON.stringify(item.key)))
                .map((item: any) => ({ ...item, disabled: true, count: 0 })),
              ...filteredItems
            ];

            if (countersMerge[counterKey].length > 5) {
              countersMerge[counterKey].sort((a: any, b: any) => {
                if (a.disabled && !b.disabled) {
                  return 1;
                }
                if (!a.disabled && b.disabled) {
                  return -1;
                }

                if (!a.disabled && !b.disabled) {
                  return 0;
                }

                return 0;
              });
            }

          } else {
            countersMerge[counterKey] = filterCountersUpcomming[typedKey];
          }
        } else {
          countersMerge[counterKey] = filterCountersUpcomming[typedKey];
        }
      });
      dispatch(activityActions.setCounters(filterCountersUpcomming));
      dispatch(activityActions.setFilterCounters(countersMerge));
    } else {
      dispatch(activityActions.setCounters(filterCountersUpcomming));
      dispatch(activityActions.setFilterCounters(null));
    }
  }
)

export const getActivity = createAsyncThunk<any, { lastSearchedLocation?: ILocation, isLoadMore: boolean, isFiltersChange: boolean, isReuseSessionKey: boolean }, { state: RootState }>(
  'activity-search/getActivity',
  async ({ lastSearchedLocation, isLoadMore, isFiltersChange, isReuseSessionKey }, { dispatch, getState }) => {

    const { activityStore } = getState();
    const { counters, filterCounters } = activityStore;

    dispatch(activityActions.setSelectedActivity(NULL_VALUE));
    dispatch(activityActions.setError(''));
    dispatch(activityActions.setErrorRecentSearches(''));

    if (!isLoadMore) {
      const { datesStore: ds } = getState();
      if (!ds.startDateSelected) {
        // An activity runs on one day, so both ends of the shared range are that day.
        // Upstream leaves `endDate` off and its own dates slice mirrors `startDate`;
        // RSI's slice stores exactly what it is given, and an absent end fails
        // `isDataValid`, which is what reddened the date field on the detail page
        // instead of letting the availability call through.
        const today = moment().format('YYYY-MM-DD');

        dispatch(datesActions.setDatesSelected({ startDate: today, endDate: today }));
      }
      dispatch(datesActions.applyDates());
      dispatch(roomsActions.applyRooms());
      dispatch(activityLocationActions.applyLocation());
    }

    // initial setting hotels to show skeleton
    if (!activityStore.activities) {
      dispatch(activityActions.setActivities({ activities: [], isLoadMore: false }));
    }

    const { activityLocationsStore } = getState();
    const { location } = activityLocationsStore;

    const locationToSearch = lastSearchedLocation ? lastSearchedLocation : location;

    //     let currentPage = activityStore.pageNumber;

    if (locationToSearch?.name) {
      dispatch(activityActions.setSearchedLocation(locationToSearch.name));
    }
    if (locationToSearch) {
      dispatch(activityActions.setLastSearchedLocation(locationToSearch));
    }

    if (!locationToSearch) {
      dispatch(activityActions.setError('Location is required'));
      dispatch(activityActions.setLoading(false));
      dispatch(activityActions.setLoadingMore(false));
      dispatch(activityActions.setLoadingFilter(false));
      return;
    }

    // cancel request if it's not pagination request
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    if (isLoadMore) {
      dispatch(activityActions.setLoadingMore(true));
    } else {
      dispatch(activityActions.setLoading(true));
      dispatch(activityActions.setIsSearch(true));
      dispatch(activityActions.setLoadingFilter(false));
      dispatch(activityActions.setPageNumber(DEFAULT_PAGE_NUMBER));
      //       currentPage = DEFAULT_PAGE_NUMBER;
    }

    if (isFiltersChange) {
      dispatch(activityActions.setPageNumber(DEFAULT_PAGE_NUMBER));
      dispatch(activityActions.setLoadingFilter(true));
      //       currentPage = DEFAULT_PAGE_NUMBER;
    }

    try {
      const { datesStore, roomsStore } = getState();
      const { startDate } = datesStore;
      const { rooms } = roomsStore;

      try {
        const resFull = await getActivityRetry(locationToSearch, isLoadMore, isReuseSessionKey, getState, abortController.signal);

        if (!isLoadMore) {
          // Store recent search only on successful first-page response; keep today + future dates only; dedupe by putting current on top
          const todayStart = moment().startOf('day');
          const recentSearchesStorage = localStorage.getItem(ACTIVITY_RECENT_SEARCHES_LABEL);
          const recentSearchesObject =
            recentSearchesStorage && !isEmpty(recentSearchesStorage) ? JSON.parse(recentSearchesStorage) : [];
          const recentSearchesFiltered = recentSearchesObject?.length
            ? recentSearchesObject.filter((search: any) =>
                moment(search.startDate, 'YYYY-MM-DD').isSameOrAfter(todayStart, 'day'),
              )
            : [];
          const recentSearchesValid =
            recentSearchesFiltered.length > 20 ? recentSearchesFiltered.slice(0, 19) : recentSearchesFiltered;

          const previewImages = getActivityRecentPreviewImages(resFull.data?.activities);

          const currentSearch: any = {
            startDate: moment(startDate, 'YYYY-MM-DD').format('YYYY-MM-DD'),
            location: {
              ...locationToSearch,
              type: locationToSearch?.realType || locationToSearch?.type,
              isoCode: locationToSearch?.isoCode,
              realType: undefined,
            },
            locationCode: locationToSearch.code,
            occupancy: {
              adultsCount: rooms.reduce((sum: number, r: any) => sum + (r?.adultsCount ?? 0), 0),
              kids: rooms.flatMap((r: any) =>
                Array.isArray(r.kids) ? r.kids.map((k: any) => (typeof k === 'object' && k != null && 'age' in k ? k.age : k)) : [],
              ),
            },
            images: previewImages,
          };

          const locationKey = getRecentSearchLocationKeyHotel(currentSearch);
          const withoutSameLocation = recentSearchesValid.filter(
            (r: any) => getRecentSearchLocationKeyHotel(r) !== locationKey,
          );
          localStorage.setItem(ACTIVITY_RECENT_SEARCHES_LABEL, JSON.stringify([currentSearch, ...withoutSameLocation]));
        }

        if (resFull.data.bounds) {
          dispatch(activityActions.setBounds(resFull.data.bounds));
        } else if (!resFull.data.activities?.length) {
          if (locationToSearch?.geoLocation?.latitude && locationToSearch?.geoLocation?.longitude) {
            dispatch(
              activityActions.setBounds({
                northEast: {
                  latitude: locationToSearch.geoLocation.latitude + reRenderNum,
                  longitude: locationToSearch.geoLocation.longitude + reRenderNum,
                },
                southWest: {
                  latitude: locationToSearch.geoLocation.latitude + reRenderNum,
                  longitude: locationToSearch.geoLocation.longitude + reRenderNum,
                },
              }),
            );
          } else {
            dispatch(activityActions.setBounds(NULL_VALUE));
          }
        }

        if (resFull.data?.sessionKey?.isInvalid) {
          dispatch(activityActions.setSessionKey(NULL_VALUE));
          UrlUtils.removeFromUrl(SESSION_KEY_LABEL);
          dispatch(getActivity({ lastSearchedLocation: locationToSearch, isLoadMore: false, isFiltersChange: false, isReuseSessionKey: false }));
          return;
        }
        dispatch(activityActions.setSessionKey(resFull.data.sessionKey));

        if (resFull.data.sessionKey) {
          dispatch(activityActions.setIsSessionExpired(false));
          // @ts-ignore
          setActivitySessionExpirationTimer(resFull.data.sessionKey, dispatch);
        }

        if (resFull.data.counters) {
          if (isFiltersChange) dispatch(mergeActivityCounters({ counters, filterCounter: filterCounters, filterCountersUpcomming: resFull.data.counters }));
          else {
            dispatch(activityActions.setCounters(resFull.data.counters));
            dispatch(activityActions.setFilterCounters(null));
          }
        }

        if (!!resFull.data.activities) {
          if (isFiltersChange) {
            dispatch(activityActions.setActivities({ activities: resFull.data.activities, isLoadMore: false }));
          } else {
            dispatch(activityActions.setActivities({ activities: resFull.data.activities, isLoadMore }));
          }
          dispatch(activitySavedPropertyActions.syncSavedIds(resFull.data.activities));
        }

        dispatch(activityActions.setLoading(false));
        dispatch(activityActions.setLoadingMore(false));
        dispatch(activityActions.setLoadingFilter(false));

      } catch (error) {
        handleGetHotelsErrors(error, dispatch);
      };


    } catch (error) {
      handleGetHotelsErrors(error, dispatch);
    }
  },
);

const handleGetHotelsErrors = (error: any, dispatch: any) => {
  console.error(error);

  // dispatch(activityActions.setMapLoading(false));
  dispatch(activityActions.setError(
    !isEmpty(error?.response?.data?.code) ?
      error?.response?.data?.code?.toString() :
      !isEmpty(error?.code) ?
        error?.code?.toString() :
        'GENERIC'));

  // error when user session ended
  if (
    error?.response?.status === SESSION_EXPIRED_STATUS &&
    error?.response?.data &&
    error?.response?.data[zeroItem] === SESSION_EXPIRED_MESSAGE
  ) {
    dispatch(activityActions.setIsSessionExpired(true));
  }

  // error triggered by axios when we cancel request
  if (!(error instanceof axios.Cancel)) {
    dispatch(activityActions.setLoadingMore(false));
    dispatch(activityActions.setLoading(false));
  }
  dispatch(activityActions.setLoadingFilter(false));
}

export const resetActivityAshevillePlayLocation = createAsyncThunk<any, void, { state: RootState }>(
  'hotel-search/resetActivityAshevillePlayLocation',
  async (_, { dispatch, getState }) => {
    const { loginStore } = getState();
    const { account } = loginStore;

    const isAshevillePlay = account?.isAshevillePlay;
    const ashevillePlayLocationCode = account?.ashevillePlayLocationCode;

    if (isAshevillePlay && ashevillePlayLocationCode) {
      try {
        const response = await getLocationByCode(ashevillePlayLocationCode as any, true);

        if (!!response) {
          const location = response.data;
          dispatch(activityLocationActions.setSelectLocationLabel(location.name as string));
          dispatch(activityLocationActions.setSelectLocation(location as ILocation));
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      dispatch(activityLocationActions.resetLocation());
    }
  }
);

export const resetActivityFull = createAsyncThunk<any, void, { state: RootState }>(
  'activity-search/resetActivityFull',
  async (_, { dispatch, getState }) => {
    const { loginStore } = getState();
    const { account } = loginStore;

    if (account?.isAshevillePlay) {
      dispatch(resetActivityAshevillePlayLocation());
    } else {
      dispatch(activityLocationActions.resetLocation());
      dispatch(activityLocationActions.setLocationKey(new Date().getTime().toString()));
    }
    dispatch(datesActions.resetDates());
    // dispatch(roomsActions.resetRooms());
    dispatch(activityFiltersActions.resetFilters(account?.isCapitalVacations));
    dispatch(activityActions.setIsSearch(false));
    dispatch(activityActions.clearActivities());
    dispatch(activityActions.setError(''));
    dispatch(activityActions.setErrorRecentSearches(''));
  }
);